import { useCallback, useMemo, useState } from "react";
import {
  DMAutomation,
  DMAutomationContent,
  DMAutomationState,
  DMAutomationTrigger,
  NotificationType,
} from "@markit/common.types";
import { GetDateWithTime, handlePluralString } from "@markit/common.utils";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import ButtonWithDropdown from "../../Buttons/ButtonWithDropdown";
import { DropdownMenuItem } from "../../DropdownMenu";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { onDMAutomationNavigatePath } from "../../../utils/navigationUtils";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { DMAutomationConfirmDeleteModal } from "../../CreatorDashboard/AutoDM/Items/DMAutomationConfirmDeleteModal";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import {
  getKeywordTriggerLabel,
  getTriggerPostLabel,
  toggleDMAutomationState,
} from "../../../utils/dmAutomationUtils";
import { useDispatch, useSelector } from "react-redux";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import CloneDMAutomationModal from "../../CreatorDashboard/AutoDM/Items/CloneDMAutomationModal";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import SocialProfilePicsLabel from "./SocialProfilePicsLabel";
import { SocialPlatformThumbnailItem } from "./SocialPlatformThumbnailItem";
import { SocialPlatformIconItem } from "./SocialPlatformIconItem";
import ActiveInactiveLabel from "../ActiveInactiveLabel";
import CustomLinkify from "../../Links/CustomLinkify";

export const AUTO_DM_ITEM_FLEX_SECTIONS = [2.5, 1, 1, 7];
// Multiple connected accounts
export const AUTO_DM_ITEM_FLEX_SECTIONS_WITH_ACCOUNTS = [2.5, 1.5, 1, 1, 7];

type AutoDMItemPreviewProps = {
  dmAutomation: DMAutomation;
  firstTrigger: DMAutomationTrigger | undefined;
  triggerCount: number;
  submissionCount: number; // for send as text automations
  triggeredTotal: number; // for send as DM automations
  dmMessageContent: DMAutomationContent | undefined; // the automation's DM_MESSAGE content, if it has one
  campaignMessage: string | undefined; // the linked campaign's initial MassText message, for send as text automations
};

export const AutoDMItemPreview = ({
  dmAutomation,
  firstTrigger,
  triggerCount,
  submissionCount,
  triggeredTotal,
  dmMessageContent,
  campaignMessage,
}: AutoDMItemPreviewProps) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { instagramAccounts, facebookPages } = useSelector(getAccountSlice);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const styles = {
    itemBorder: { border: `1px solid ${theme.SubText.color}` },
    itemCard: { borderRadius: 8, padding: 4, maxWidth: 400 },
  };

  const navigateDMAutomationItem = useCallback(
    () => onDMAutomationNavigatePath(navigate, dmAutomation.id),
    [dmAutomation.id, navigate]
  );

  const changeDMAutomationState = useCallback(async () => {
    await toggleDMAutomationState(
      dmAutomation.createdBy,
      dmAutomation.id,
      dmAutomation.state === DMAutomationState.ACTIVE
        ? DMAutomationState.INACTIVE
        : DMAutomationState.ACTIVE
    );
    showNotificationBanner(
      dispatch,
      dmAutomation.state === DMAutomationState.ACTIVE
        ? "Paused Automation"
        : "Reactivated Automation",
      NotificationType.AFFIRMATIVE
    );
  }, [dmAutomation.createdBy, dmAutomation.id, dmAutomation.state, dispatch]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Clone",
        icon: "ion:copy",
        key: "clone",
        onPress: () => {
          setShowCloneModal(true);
          setShowDropdown(false);
        },
      },
    ];

    if (dmAutomation.state !== DMAutomationState.DRAFT) {
      items.push({
        title:
          dmAutomation.state === DMAutomationState.ACTIVE
            ? "Pause"
            : "Reactivate",
        icon:
          dmAutomation.state === DMAutomationState.ACTIVE
            ? "ion:pause-circle"
            : "ion:play-circle",
        key: "pause_reactivate",
        onPress: () => {
          changeDMAutomationState();
          setShowDropdown(false);
        },
      });
    }

    items.push({
      title: "Delete",
      icon: "ion:trash",
      key: "delete",
      onPress: () => {
        setShowDeleteModal(true);
        setShowDropdown(false);
      },
      isDestructive: true,
    });

    return items;
  }, [dmAutomation.state, changeDMAutomationState]);

  const hasMultipleAccounts = useMemo(
    () => instagramAccounts.length + facebookPages.length > 1,
    [instagramAccounts.length, facebookPages.length]
  );

  const flexSections = useMemo(
    () =>
      hasMultipleAccounts
        ? AUTO_DM_ITEM_FLEX_SECTIONS_WITH_ACCOUNTS
        : AUTO_DM_ITEM_FLEX_SECTIONS,
    [hasMultipleAccounts]
  );

  // Only a trigger scoped to one specific post/reel has a real thumbnail to show -
  // Next/All Posts triggers don't, so the card drops the thumbnail column entirely.
  const hasTriggerThumbnail = useMemo(
    () => !!firstTrigger?.mediaUrl,
    [firstTrigger?.mediaUrl]
  );

  const triggerTitle = useMemo(
    () =>
      firstTrigger
        ? `${getTriggerPostLabel(firstTrigger)} · ${getKeywordTriggerLabel(
            firstTrigger.keywordRules ?? [],
            firstTrigger
          )}`
        : "Can't display trigger details",
    [firstTrigger]
  );

  // Send as text automations have their message on the linked campaign's initial
  // MassText; send as DM automations have it on their own DM_MESSAGE content doc.
  const previewMessage = useMemo(
    () =>
      dmAutomation.campaignId ? campaignMessage : dmMessageContent?.message,
    [dmAutomation.campaignId, campaignMessage, dmMessageContent]
  );

  const renderHorizontalLine = useMemo(
    () => (
      <div style={{ width: 22 }}>
        <HorizontalDivider />
      </div>
    ),
    []
  );

  const renderInfoIcon = useCallback(
    (type: "Triggered" | "Sent", numCount: number) => (
      <div className="AlignedRow" style={{ gap: 4 }}>
        <span className="smallBodySubtext" style={{ whiteSpace: "nowrap" }}>
          <span style={{ color: Colors.BLACK, fontWeight: 500, fontSize: 14 }}>
            {numCount}
          </span>{" "}
          {type}
        </span>
      </div>
    ),
    []
  );

  return (
    <>
      <div className="AlignedRow" style={{ height: 64, gap: 14 }}>
        <div
          className="AlignedRowSelect"
          onClick={navigateDMAutomationItem}
          style={{ flex: flexSections[0], gap: 10 }}
        >
          {hasMultipleAccounts ? (
            <SocialPlatformIconItem
              platform={dmAutomation.platform}
              size={24}
              noBorder
            />
          ) : null}
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <span
              className="AboutSubtitle OneLineText"
              style={{ fontSize: 14 }}
            >
              {dmAutomation.title}
            </span>
            <div className="AlignedRow" style={{ gap: 4 }}>
              <Icon
                icon={
                  dmAutomation.campaignId
                    ? "ion:paper-plane"
                    : "ion:chatbubble-ellipses"
                }
                height={14}
              />
              <span className="smallBodySubtext">
                {dmAutomation.campaignId ? "Send as Text" : "Send as DM"}
              </span>
            </div>
            <span className="smallBodySubtext">
              Updated: {GetDateWithTime(dmAutomation.updatedAt, true)}
            </span>
          </div>
        </div>
        {hasMultipleAccounts && dmAutomation.igAccountIds.length > 0 ? (
          <div
            className="ColumnNormal"
            style={{ gap: 4, flex: flexSections[1], maxWidth: 100 }}
          >
            <SocialProfilePicsLabel
              igAccountIds={dmAutomation.igAccountIds}
              platform={dmAutomation.platform}
            />
          </div>
        ) : null}
        <div style={{ flex: flexSections[hasMultipleAccounts ? 2 : 1] }}>
          <ActiveInactiveLabel
            isActive={dmAutomation.state !== DMAutomationState.INACTIVE}
          />
        </div>
        <div
          className="ColumnNormal"
          style={{ flex: flexSections[hasMultipleAccounts ? 3 : 2], gap: 4 }}
        >
          <StandardBorderedContainer
            containerStyles={{
              borderRadius: 8,
              paddingInline: 9,
              paddingBlock: 7,
              backgroundColor: Colors.WHITE1,
              width: "fit-content",
              ...styles.itemBorder,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 2 }}>
              {renderInfoIcon("Triggered", triggeredTotal)}
              {dmAutomation.campaignId
                ? renderInfoIcon("Sent", submissionCount ?? 0)
                : null}
            </div>
          </StandardBorderedContainer>
        </div>
        <div
          className="AlignedRowSpaced"
          style={{
            flex: flexSections[hasMultipleAccounts ? 4 : 3],
            gap: 24,
            paddingLeft: 14,
          }}
        >
          <div className="AlignedRow" style={{ gap: 4 }}>
            <StandardBorderedContainer
              containerStyles={{
                ...styles.itemCard,
                padding: hasTriggerThumbnail ? 4 : 10,
              }}
            >
              <div className="AlignedRow" style={{ gap: 7 }}>
                {hasTriggerThumbnail ? (
                  <SocialPlatformThumbnailItem
                    mediaUrl={firstTrigger?.mediaUrl}
                    platform={dmAutomation.platform}
                    size={40}
                  />
                ) : null}
                <div className="ColumnNormal" style={{ gap: 2 }}>
                  <span className="OneLineText bodySmallMedium">
                    {triggerTitle}
                  </span>
                  {previewMessage ? (
                    <CustomLinkify noUnderline>
                      <span
                        className="OneLineText minyBodySubtext"
                        style={{ maxWidth: 320 }}
                      >
                        &quot;{previewMessage}&quot;
                      </span>
                    </CustomLinkify>
                  ) : (
                    <span className="minyBodySubtext" style={{ maxWidth: 320 }}>
                      No message set
                    </span>
                  )}
                </div>
              </div>
            </StandardBorderedContainer>
            {triggerCount > 1 ? (
              <>
                {renderHorizontalLine}
                <StandardBorderedContainer
                  containerStyles={{
                    ...styles.itemCard,
                    paddingBlock: 10,
                    width: 80,
                  }}
                >
                  <div className="Centering">
                    <span style={{ fontSize: 10 }}>
                      +{triggerCount - 1}{" "}
                      {handlePluralString("Trigger", triggerCount - 1)}
                    </span>
                  </div>
                </StandardBorderedContainer>
              </>
            ) : null}
          </div>
          <ButtonWithDropdown
            buttonLabel="View"
            textColor={Colors.BLACK}
            isOpenDropdown={showDropdown}
            setIsOpenDropdown={setShowDropdown}
            onPress={navigateDMAutomationItem}
            dropdownItems={dropdownItems}
            containerStyles={styles.itemBorder}
          />
        </div>
      </div>
      {showDeleteModal ? (
        <DMAutomationConfirmDeleteModal
          id={dmAutomation.id}
          dmAutomationId={dmAutomation.id}
          type="Automation"
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
        />
      ) : null}
      {showCloneModal ? (
        <CloneDMAutomationModal
          dmAutomation={dmAutomation}
          isModalVisible={showCloneModal}
          setIsModalVisible={setShowCloneModal}
        />
      ) : null}
    </>
  );
};
